import { lazy, memo, Suspense, useCallback } from "react";
import { Button } from "react-bootstrap";
import CountUp from "react-countup";
import { useNavigate } from "react-router-dom";
import "../css/MemberBenefits.css";
import { useTranslation } from "react-i18next";

const Card = lazy(() => import("./Card"));

const PROFILE_IMG_SIZE = 80;

const CLIP_STYLE: React.CSSProperties = {
  clipPath: "ellipse(79% 91% at 50% 9%)",
};

const CARD_PLACEHOLDER_CLASS = "m-4 border container-bg p-4 p-md-5 rounded-4";
const CARD_PLACEHOLDER_STYLE: React.CSSProperties = { minHeight: 180 };

const PLACEHOLDER_INDICES = [0, 1, 2] as const;

interface MembershipCardData {
  logo: string;
  titleKey: string;
  descriptionKey: string;
  membershipName: string;
}

const MEMBERSHIP_CARDS: MembershipCardData[] = [
  {
    logo: "/b.png",
    titleKey: "membership.businessTitle",
    descriptionKey: "membership.businessDesc",
    membershipName: "Business Membership",
  },
  {
    logo: "/e.png",
    titleKey: "membership.executiveTitle",
    descriptionKey: "membership.executiveDesc",
    membershipName: "Executive Membership",
  },
  {
    logo: "/y.png",
    titleKey: "membership.earlyStageTitle",
    descriptionKey: "membership.earlyStageDesc",
    membershipName: "Early Stage Membership",
  },
];

function MemberBenefits() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToMembershipCheckout = useCallback(
    (membershipTitle: string) => {
      navigate(
        `/register-user?membership=${encodeURIComponent(membershipTitle)}`
      );
    },
    [navigate]
  );

  return (
    <div className="w-100">
      <div className="p-2 p-md-5 member-benefits-landing d-flex flex-column gap-3 justify-items-center justify-content-center text-white text-center">
        <h1 className="px-md-5 px-1 display-4 fw-bold">
          {t("membership.chooseTitle")}
        </h1>
        <p className=" px-md-5 px-1 fs-4 fw-semibold text-light">
          {t("membership.chooseSubtitle")}
        </p>
        <div>
          <Button className="px-5">{t("membership.viewMemberships")}</Button>
        </div>
        <div className="d-flex align-items-center justify-content-center gap-4 gap-md-5 text-white px-4 py-3">
          <div className="image-stack position-relative d-flex">
            <img
              src="/SMBF-23.jpeg"
              alt="SMBF members"
              className="profile-img z-2"
              width={PROFILE_IMG_SIZE}
              height={PROFILE_IMG_SIZE}
              loading="eager"
              decoding="async"
            />
            <img
              src="/SMBF-24.jpeg"
              alt="SMBF network"
              className="profile-img z-1 position-absolute second-img"
              width={PROFILE_IMG_SIZE}
              height={PROFILE_IMG_SIZE}
              loading="lazy"
              decoding="async"
            />
            <img
              src="/SMBF-25.jpeg"
              alt="SMBF community"
              className="profile-img z-2 position-absolute third-img"
              width={PROFILE_IMG_SIZE}
              height={PROFILE_IMG_SIZE}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="ms-5 px-5">
            <h3 className="mb-0 fw-bold text-white">
              {" "}
              <CountUp end={5000} duration={1} separator="," suffix="+" />
            </h3>
            <h5 className="mb-0 text-light">{t("common.network")}</h5>
          </div>
        </div>
      </div>
      <div className="card-bg">
        <div className="bg-light py-4 text-center pb-5" style={CLIP_STYLE}>
          <h2>
            {t("membership.selectTailored")}
          </h2>
        </div>
        <div className="py-5" id="membership-cards">
          <Suspense
            fallback={
              <>
                {PLACEHOLDER_INDICES.map((i) => (
                  <div
                    key={i}
                    className={CARD_PLACEHOLDER_CLASS}
                    style={CARD_PLACEHOLDER_STYLE}
                    aria-hidden="true"
                  />
                ))}
              </>
            }
          >
            {MEMBERSHIP_CARDS.map((card) => (
              <Card
                key={card.titleKey}
                logo={card.logo}
                title={t(card.titleKey)}
                description={t(card.descriptionKey)}
                action={() => goToMembershipCheckout(card.membershipName)}
              />
            ))}
          </Suspense>
        </div>
      </div>
    </div>
  );
}

MemberBenefits.displayName = "MemberBenefits";

export default memo(MemberBenefits);
